import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import {
  FaBook,
  FaCheckCircle,
  FaPlayCircle,
  FaRupeeSign,
  FaArrowRight,
  FaClock,
  FaStar,
  FaUserGraduate,
  FaHeadset,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../components/common/Header";
import ContactModal from "../../components/common/ContactModal";
import ActivityCalendar from "./components/ActivityCalendar";
import RecentUpdates from "./components/RecentUpdates";
import MotivationQuote from "./components/MotivationQuote";
import DefaultPasswordModal from "../../components/student/DefaultPasswordModal";
import ReminderPasswordModal from "../../components/student/ReminderPasswordModal";
import { useAuth } from "../../context/AuthContext";
import CourseService from "../../services/courseService";
import StudentService from "../../services/studentService";
import API from "../../services/api";

import "../../styles/StudentDashboard.css";

const StudentDashboard = () => {
  const navigate = useNavigate();
  const {
    user,
    showDefaultPasswordModal,
    showReminderModal,
    dismissDefaultPasswordModal,
    dismissReminderModal,
    onPasswordChangeSuccess,
  } = useAuth();

  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [streak, setStreak] = useState(0);
  const [summary, setSummary] = useState({
    total_courses: 0,
    total_fees: 0,
    total_paid: 0,
    total_due: 0,
  });

  useEffect(() => {
    loadDashboardData();
    trackLoginActivity();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Get enrolled courses
      const coursesResponse = await StudentService.getStudentCourses(user.id);
      setEnrolledCourses(coursesResponse.data || []);

      // Get all courses for recommendations
      const allCoursesResponse = await CourseService.getAllCourses();
      const allCourses = allCoursesResponse.data || [];
      const enrolledIds = (coursesResponse.data || []).map((c) => c.course_id);
      const available = allCourses.filter((c) => !enrolledIds.includes(c.id));
      setAvailableCourses(available);

      // Get payment summary
      const summaryResponse = await StudentService.getStudentSummary(user.id);
      setSummary(
        summaryResponse.data || {
          total_courses: 0,
          total_fees: 0,
          total_paid: 0,
          total_due: 0,
        },
      );

      // Calculate streak from activity
      await calculateStreak();
    } catch (error) {
      toast.error("Failed to load dashboard data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const trackLoginActivity = async () => {
    try {
      await API.post(`/student/${user.id}/activity/track`);
    } catch (error) {
      console.error("Failed to track activity:", error);
    }
  };

  const calculateStreak = async () => {
    try {
      const response = await API.get(`/student/${user.id}/activity`);
      const activities = response.data.data || [];
      const dates = activities.map((a) => a.activity_date).sort();

      let currentStreak = 0;
      let today = new Date().toISOString().split("T")[0];

      for (let i = dates.length - 1; i >= 0; i--) {
        if (dates[i] === today) {
          currentStreak++;
          let prevDate = new Date(today);
          prevDate.setDate(prevDate.getDate() - 1);
          today = prevDate.toISOString().split("T")[0];
        } else {
          break;
        }
      }

      setStreak(currentStreak);
    } catch (error) {
      console.error("Failed to calculate streak:", error);
    }
  };

  const handleViewCourse = (courseId) => {
    navigate(`/student/courses/${courseId}`);
  };

  const handlePublicViewCourse = (courseId) => {
    window.open(`/courses/${courseId}`, "_blank");
  };

  const handleRequestEnrollment = (course) => {
    setSelectedCourse(course);
    setShowContactModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const completedCount = enrolledCourses.filter(
    (c) => c.progress === 100,
  ).length;

  if (loading) {
    return (
      <div>
        <Header />
        <div className="dashboard-loading">
          <Spinner animation="border" variant="primary" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="student-dashboard-wrapper">
        {/* Welcome Hero Section */}
        <section className="dashboard-hero">
          <div className="hero-animated-bg"></div>
          <div className="floating-emoji emoji-1">📚</div>
          <div className="floating-emoji emoji-2">🎓</div>
          <div className="floating-emoji emoji-3">💻</div>
          <div className="floating-emoji emoji-4">🏆</div>
          <div className="floating-emoji emoji-5">⭐</div>
          <div className="floating-emoji emoji-6">🚀</div>

          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-greeting">
                Welcome back, {user?.name?.split(" ")[0]}!{" "}
                <span className="wave-hand">👋</span>
              </h1>
              <p className="hero-message">
                Continue your learning journey and achieve your goals.
              </p>
              {streak > 0 && (
                <div className="streak-badge-hero">
                  <span className="streak-fire">🔥</span>
                  <span className="streak-text">
                    {streak} day learning streak!
                  </span>
                </div>
              )}
            </div>
            <div className="hero-stats">
              <div className="hero-stat">
                <div className="hero-stat-icon bg-primary">
                  <FaBook />
                </div>
                <div className="hero-stat-info">
                  <span className="hero-stat-value">
                    {enrolledCourses.length}
                  </span>
                  <span className="hero-stat-label">Enrolled</span>
                </div>
              </div>
              <div className="hero-stat">
                <div className="hero-stat-icon bg-success">
                  <FaCheckCircle />
                </div>
                <div className="hero-stat-info">
                  <span className="hero-stat-value">{completedCount}</span>
                  <span className="hero-stat-label">Completed</span>
                </div>
              </div>
              <div className="hero-stat">
                <div className="hero-stat-icon bg-info">
                  <FaRupeeSign />
                </div>
                <div className="hero-stat-info">
                  <span className="hero-stat-value">{formatCurrency(summary.total_due)}</span>
                  <span className="hero-stat-label">Due Amount</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Calendar and Recent Updates Row */}
        <Container className="dashboard-container">
          <Row className="g-4">
            <Col lg={5}>
              <ActivityCalendar studentId={user?.id} />
            </Col>
            <Col lg={7}>
              <RecentUpdates studentId={user?.id} />
            </Col>
          </Row>

          {/* Motivation Quote Section */}
          <MotivationQuote />

          {/* Quick Actions */}
          <Row className="mt-4 mb-5">
            <Col>
              <div className="quick-actions-grid">
                <div
                  className="quick-action-item"
                  onClick={() => navigate("/student/available")}
                >
                  <div className="quick-action-icon bg-primary-soft">
                    <FaBook />
                  </div>
                  <h6>Browse Courses</h6>
                  <small>Explore new courses</small>
                </div>
                <div
                  className="quick-action-item"
                  onClick={() => navigate("/student/courses")}
                >
                  <div className="quick-action-icon bg-success-soft">
                    <FaPlayCircle />
                  </div>
                  <h6>My Courses</h6>
                  <small>Continue learning</small>
                </div>
                <div
                  className="quick-action-item"
                  onClick={() => setShowContactModal(true)}
                >
                  <div className="quick-action-icon bg-info-soft">
                    <FaHeadset />
                  </div>
                  <h6>Contact Support</h6>
                  <small>Get help</small>
                </div>
                <div
                  className="quick-action-item"
                  onClick={() => navigate("/student/profile")}
                >
                  <div className="quick-action-icon bg-warning-soft">
                    <FaUserGraduate />
                  </div>
                  <h6>My Profile</h6>
                  <small>Update details</small>
                </div>
              </div>
            </Col>
          </Row>

          {/* Recommended Courses Section */}
          <Row className="mb-4">
            <Col>
              <div className="section-header">
                <h4 className="section-title">
                  <FaStar className="me-2 text-warning" />
                  Recommended for You
                </h4>
                <p className="section-subtitle">
                  Based on your interests and learning history
                </p>
              </div>
            </Col>
          </Row>

          {availableCourses.length === 0 ? (
            <Card className="empty-state-card text-center p-5">
              <FaBook size={60} className="text-muted mb-3 opacity-50" />
              <h5>No Recommended Courses</h5>
              <p className="text-muted">
                Check back later for new course recommendations.
              </p>
            </Card>
          ) : (
            <Row className="g-4">
              {availableCourses.slice(0, 3).map((course) => (
                <Col md={6} lg={4} key={course.id}>
                  <Card className="dashboard-course-card h-100">
                    {course.image_url && (
                      <Card.Img
                        variant="top"
                        src={course.image_url}
                        className="course-card-img"
                      />
                    )}
                    <Card.Body>
                      <Badge bg="warning" className="mb-2">
                        Recommended
                      </Badge>
                      <Card.Title className="course-card-title">
                        {course.name}
                      </Card.Title>
                      <Card.Text className="course-card-text text-muted small">
                        {course.description?.substring(0, 80)}...
                      </Card.Text>
                      <div className="course-meta mb-3">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">
                            <FaClock className="me-1" /> {course.duration}
                          </span>
                          <span className="fw-bold text-primary">
                            <FaRupeeSign className="me-1" />{" "}
                            {parseFloat(course.price).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="flex-grow-1"
                          onClick={() => handlePublicViewCourse(course.id)}
                        >
                          View Details
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="flex-grow-1"
                          onClick={() => handleRequestEnrollment(course)}
                        >
                          Request Enrollment
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {availableCourses.length > 3 && (
            <div className="text-center mt-4">
              <Button
                variant="link"
                onClick={() => navigate("/student/available")}
              >
                View All Recommended Courses <FaArrowRight className="ms-2" />
              </Button>
            </div>
          )}
        </Container>

        {/* Contact Modal */}
        <ContactModal
          show={showContactModal}
          onHide={() => setShowContactModal(false)}
          courseName={selectedCourse?.name}
          courseId={selectedCourse?.id}
        />

        {/* Default Password Modal - Shows when student has default password "123456" and hasn't seen it before */}
        <DefaultPasswordModal
          show={showDefaultPasswordModal}
          onHide={() => dismissDefaultPasswordModal(user?.id)}
          onSuccess={onPasswordChangeSuccess}
          onCancel={() => dismissDefaultPasswordModal(user?.id)}
          userId={user?.id}
        />

        {/* 6-Month Reminder Modal - Shows every 180 days after last password change */}
        <ReminderPasswordModal
          show={showReminderModal}
          onHide={() => dismissReminderModal(user?.id)}
          onCancel={() => dismissReminderModal(user?.id)}
          userId={user?.id}
        />
      </div>
    </div>
  );
};

export default StudentDashboard;